/**
 * SchoolEvent Normalized Contract
 *
 * The source-agnostic contract the Synced Events / Event Spine read path speaks.
 * Every event source (BB calendar events, BB athletics, native TicketedEvent,
 * future iCal feeds) projects onto this shape through a per-source projector
 * function. The web Explorer page, the admin-gated `GET /v1/synced-events`
 * endpoint, and the (future) public events page all speak only this contract —
 * no Blackbaud-specific fields leak past the projector boundary.
 *
 * Design decisions (Unified Event Spine — KTD3, KTD4):
 * - `source` is provenance, NOT a key discriminator for storage — the spine is
 *   a LOGICAL union over per-source tables. Sources: 'blackbaud_events' (BB
 *   calendar+section, one namespace), 'blackbaud_athletics', 'ticketed' (native
 *   TicketedEvent), 'ical' (placeholder for future iCal feeds — no projector
 *   ships in P0).
 * - `audienceScope` is category-derived for BB events and section-derived for
 *   athletics (see SchoolEventAudienceScopeSchema below).
 * - `capabilities` is an ARRAY, not a Set — Zod sets don't JSON-serialize.
 * - `editable` capability is NOT included yet (every synced row is read-only;
 *   add when the first editable source lands — KTD3).
 * - Athletics-only rich fields (`homeAway`, `opponents`, `location`, the
 *   `is_practice` capability) are populated ONLY when `source==='blackbaud_athletics'`.
 *   Consumers feature-detect via `capabilities[]`, never by branching on source.
 *
 * Address/opponent shapes reuse the AthleticAddress/AthleticOpponent schemas
 * from `athletic-enrichment.ts` — athletics is the only source that fills them.
 *
 * See: docs/_archive/2026-10-17-plans-sweep/plans/2026-06-20-canonical-event-spine-plan.md (U1, SCH-8)
 *      docs/brainstorms/2026-06-20-canonical-event-spine-design.md (R-B, R-H)
 */

import { z } from 'zod'
import { AthleticAddressSchema, AthleticOpponentSchema } from './athletic-enrichment'

// ---------------------------------------------------------------------------
// Discriminator + capability enum
// ---------------------------------------------------------------------------

/**
 * Source provenance — which system synced this event. Lowercase-snake by
 * convention. 'blackbaud_events' covers both BB calendar AND BB section events
 * (one endpoint/namespace — design R-B). 'ical' is a placeholder; no iCal
 * projector ships in P0 (design R-G).
 */
export const SchoolEventSourceSchema = z.enum(['blackbaud_events', 'blackbaud_athletics', 'ticketed', 'ical'])
export type SchoolEventSource = z.infer<typeof SchoolEventSourceSchema>

/**
 * Audience scope — who an event is for, source-neutral.
 *
 * - `school_wide`     — visible to the whole school (BB PageContent categories,
 *                       ticketed events).
 * - `community_group` — the event's BB website-calendar category has `type ===
 *                       'Community'`. ⚠️ It does NOT mean the event belongs to a
 *                       club or community group, and it names no audience: BB
 *                       calendar events attach only to school-wide website
 *                       categories, so this is a CATEGORY LABEL, not a roster.
 * - `activity`        — likewise, BB category `type === 'Activity'`.
 * - `section`         — **athletics ONLY.** An athletics team maps to an internal
 *                       Section via `resolvedSectionId`, so an athletics row can
 *                       be section-scoped (see `audienceSectionPublicId`). This is
 *                       the ONLY member that names a real audience.
 *
 * IMPORTANT INVARIANT: BB **calendar/events** (`source==='blackbaud_events'`)
 * are CATEGORY-scoped and must NEVER be `'section'`. The S1 spike proved BB
 * calendar events carry no `section_id` and academic classes publish no calendar
 * events (design R-H). `'section'` exists in this enum solely for athletics. The
 * "never academic SECTION for events" rule is enforced later at the
 * projector/derivation layer (U5/U8/U9), NOT by this enum — the enum is the
 * union of every source's possible scopes.
 */
export const SchoolEventAudienceScopeSchema = z.enum(['school_wide', 'section', 'community_group', 'activity'])
export type SchoolEventAudienceScope = z.infer<typeof SchoolEventAudienceScopeSchema>

/**
 * Capability tags — what rich fields this event row carries.
 * Consumers check `capabilities.includes('has_map')` etc.
 * Note: 'editable' is NOT included yet (all synced rows are read-only).
 *
 * `is_practice` (and the athletics rich fields it travels with) is populated
 * ONLY when `source==='blackbaud_athletics'`.
 */
export const SchoolEventCapabilitySchema = z.enum(['has_opponents', 'has_address', 'has_map', 'is_practice'])
export type SchoolEventCapability = z.infer<typeof SchoolEventCapabilitySchema>

// ---------------------------------------------------------------------------
// Status enum
// ---------------------------------------------------------------------------

export const SchoolEventStatusSchema = z.enum(['scheduled', 'cancelled', 'rescheduled'])
export type SchoolEventStatus = z.infer<typeof SchoolEventStatusSchema>

// ---------------------------------------------------------------------------
// Location block
// ---------------------------------------------------------------------------

/**
 * Optional location sub-object on a SchoolEvent. All parts may be absent —
 * athletics coverage is school-dependent.
 */
export const SchoolEventLocationSchema = z.object({
  /** Human-readable venue name (e.g. "Lovett Upper School Gym"). */
  locationName: z.string().nullable().optional(),
  /** Precomputed clean maps query the client feeds directly to an OS maps URL. */
  mapsQuery: z.string().nullable().optional(),
  /** Structured address block (BB coverage varies per school). */
  address: AthleticAddressSchema.nullable().optional()
})
export type SchoolEventLocation = z.infer<typeof SchoolEventLocationSchema>

// ---------------------------------------------------------------------------
// SchoolEvent entity
// ---------------------------------------------------------------------------

/**
 * Normalized school event. Always sourced from a projector function; never
 * raw DB rows. Consumers MUST NOT branch on Blackbaud-specific fields — use
 * `capabilities` to feature-detect what rich fields are present.
 */
export const SchoolEventSchema = z.object({
  /** Public CUID — never the internal DB bigint. */
  id: z.string(),
  /** Which system synced this event. */
  source: SchoolEventSourceSchema,
  /** Human-readable event title (game name, practice label, etc.). */
  title: z.string().nullable().optional(),
  /**
   * Date string: the BB-anchored game date re-anchored to the school's
   * local timezone (KTD4). ISO 8601 date-or-datetime string.
   */
  startDate: z.string().nullable().optional(),
  /**
   * Free-text start time as BB returned it (e.g. "7:00 PM"). Consumers
   * should use this for display; rely on `startDate` for sorting only.
   */
  startTimeText: z.string().nullable().optional(),
  /** Optional end date (ISO 8601 string). Athletics rarely supplies this. */
  endDate: z.string().nullable().optional(),
  /** Home / Away / Neutral — athletics only (`source==='blackbaud_athletics'`). */
  homeAway: z.string().nullable().optional(),
  /** Opponent(s) for this game — athletics only. Empty array when none. */
  opponents: z.array(AthleticOpponentSchema).nullable().optional(),
  /** Computed status with explicit precedence (cancelled wins over rescheduled). */
  status: SchoolEventStatusSchema,
  /** Free-text reschedule note from the source. Only set when status=rescheduled. */
  rescheduledNote: z.string().nullable().optional(),
  /**
   * Optional location / address block — athletics only. Absent when coverage is
   * thin. (`source==='blackbaud_athletics'`.)
   */
  location: SchoolEventLocationSchema.nullable().optional(),
  /**
   * Capability tags — what rich fields this event actually has. Check
   * `capabilities.includes('has_map')` rather than testing mapsQuery directly.
   */
  capabilities: z.array(SchoolEventCapabilitySchema),
  // -------------------------------------------------------------------------
  // Source-neutral spine fields (Unified Event Spine — SCH-8).
  //
  // All OPTIONAL for now: population is wired in later units (U5/U8/U9). Keeping
  // them optional makes this contract change non-breaking for the existing
  // athletics projector, which does not yet emit them.
  // -------------------------------------------------------------------------
  /**
   * Who this event is for. Derived per source: category association for BB
   * events; internal `resolvedSectionId` for athletics. BB events are NEVER
   * `'section'` (invariant enforced at the projector — see schema doc above).
   */
  audienceScope: SchoolEventAudienceScopeSchema.optional(),
  /**
   * Public CUID of the internal Section this event is scoped to — **athletics
   * only** (sourced from `AthleticGame.resolvedSectionId`). `null` when the
   * athletics game has no resolved section (degraded); never set for BB events.
   */
  audienceSectionPublicId: z.string().nullable().optional(),
  /**
   * Whether this event may surface on the unauthenticated public events page.
   * Derived (not copied): for BB events, `(≥1 associated category public) AND
   * audienceScope==='school_wide'` (FERPA veto — U8). Defaults false at the DB.
   */
  isPublic: z.boolean().optional(),
  /** Provenance: when our sync last wrote this row (ISO 8601, nullable). */
  syncedAt: z.string().nullable().optional(),
  /** Provenance: source-reported last-modified instant (ISO 8601, nullable). */
  sourceLastModified: z.string().nullable().optional()
})
export type SchoolEvent = z.infer<typeof SchoolEventSchema>
